/**
 * Admin API client — calls API Gateway with Cognito JWT authentication.
 *
 * All tier data operations go through backend Lambda handlers,
 * authenticated via the Cognito JWT token in the Authorization header.
 */

import axios, { type AxiosInstance } from 'axios';
import { getAppConfig } from '@/config/api';
import { getIdToken, signOut } from './auth';

import type {
  TierRecord,
  AccessRule,
  UpgradeRule,
  Promotion,
  CsiNameMapping,
  SeedData,
} from '@/types';

// ---------------------------------------------------------------------------
// Axios client with JWT auth + API key
// ---------------------------------------------------------------------------

function createClient(): AxiosInstance {
  const config = getAppConfig();
  const instance = axios.create({
    baseURL: config.apiBaseUrl,
    timeout: 30000,
  });

  instance.interceptors.request.use((reqConfig) => {
    const token = getIdToken();
    if (token) {
      reqConfig.headers.Authorization = `Bearer ${token}`;
    }
    // Send the API key from config.json
    const appConfig = getAppConfig();
    if (appConfig.apiKey) {
      reqConfig.headers['x-api-key'] = appConfig.apiKey;
    }
    return reqConfig;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        signOut();
        window.location.reload();
      }
      throw error;
    }
  );

  return instance;
}

let client: AxiosInstance | null = null;

function getClient(): AxiosInstance {
  if (!client) client = createClient();
  return client;
}

/** Reset the client (e.g., after config load or sign-in). */
export function resetClient(): void {
  client = null;
}

// ---------------------------------------------------------------------------
// Helper to unwrap API response envelope
// ---------------------------------------------------------------------------

function unwrap<T>(response: { data: { data?: T; success?: boolean; error?: string } }): T {
  const body = response.data;
  if (body.success === false) {
    throw new Error(body.error || 'API request failed');
  }
  return body.data as T;
}

// ---------------------------------------------------------------------------
// Tier operations
// ---------------------------------------------------------------------------

export async function getTiers(region?: string): Promise<TierRecord[]> {
  const params = region ? { region } : {};
  const data = unwrap<{ tiers: TierRecord[] }>(await getClient().get('/admin/tiers', { params }));
  return data.tiers || [];
}

export async function getTier(region: string, tierId: string): Promise<TierRecord> {
  const data = unwrap<{ tier: TierRecord }>(await getClient().get(`/admin/tiers/${region}/${tierId}`));
  return data.tier;
}

export async function saveTier(tier: TierRecord): Promise<void> {
  await getClient().put('/admin/tiers', tier);
}

export async function deleteTier(region: string, tierId: string): Promise<void> {
  await getClient().delete(`/admin/tiers/${region}/${tierId}`);
}

// ---------------------------------------------------------------------------
// Upgrade rule operations
// ---------------------------------------------------------------------------

export async function getUpgradeRules(fromRegion?: string): Promise<UpgradeRule[]> {
  const params = fromRegion ? { fromRegion } : {};
  const data = unwrap<{ upgrade_rules: UpgradeRule[] }>(await getClient().get('/admin/upgrade-rules', { params }));
  return data.upgrade_rules || [];
}

export async function saveUpgradeRule(rule: UpgradeRule): Promise<void> {
  await getClient().put('/admin/upgrade-rules', rule);
}

export async function deleteUpgradeRule(
  fromRegion: string, fromTierId: string, fromType: string,
  toRegion: string, toTierId: string, toType: string
): Promise<void> {
  await getClient().delete('/admin/upgrade-rules', {
    data: { fromRegion, fromTierId, fromType, toRegion, toTierId, toType },
  });
}

// ---------------------------------------------------------------------------
// Access rule operations
// ---------------------------------------------------------------------------

export async function getAccessRules(homeRegion?: string): Promise<AccessRule[]> {
  const params = homeRegion ? { homeRegion } : {};
  const data = unwrap<{ access_rules: AccessRule[] }>(await getClient().get('/admin/access-rules', { params }));
  return data.access_rules || [];
}

export async function saveAccessRule(rule: AccessRule): Promise<void> {
  await getClient().put('/admin/access-rules', rule);
}

export async function deleteAccessRule(
  homeRegion: string, homeTierId: string,
  destRegion: string, destTierId: string
): Promise<void> {
  await getClient().delete('/admin/access-rules', {
    data: { homeRegion, homeTierId, destRegion, destTierId },
  });
}

// ---------------------------------------------------------------------------
// CSI name map operations
// ---------------------------------------------------------------------------

export async function getCsiMappings(): Promise<CsiNameMapping[]> {
  const data = unwrap<{ csi_mappings: CsiNameMapping[] }>(await getClient().get('/admin/csi-map'));
  return data.csi_mappings || [];
}

export async function saveCsiMapping(mapping: CsiNameMapping): Promise<void> {
  await getClient().put('/admin/csi-map', mapping);
}

export async function deleteCsiMapping(pattern: string): Promise<void> {
  await getClient().delete(`/admin/csi-map/${encodeURIComponent(pattern)}`);
}

// ---------------------------------------------------------------------------
// Promotion operations
// ---------------------------------------------------------------------------

export async function getPromotions(): Promise<Promotion[]> {
  const data = unwrap<{ promotions: Promotion[] }>(await getClient().get('/admin/promotions'));
  return data.promotions || [];
}

export async function savePromotion(promo: Promotion): Promise<void> {
  if (promo.promoId) {
    await getClient().put('/admin/promotions', promo);
  } else {
    await getClient().post('/admin/promotions', promo);
  }
}

export async function deletePromotion(promoId: string): Promise<void> {
  await getClient().delete(`/admin/promotions/${encodeURIComponent(promoId)}`);
}

// ---------------------------------------------------------------------------
// Seed database
// ---------------------------------------------------------------------------

export async function seedDatabase(data: SeedData): Promise<{ count: number }> {
  const response = await getClient().post('/admin/seed', data);
  return unwrap<{ count: number }>(response);
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

export async function getRegions(): Promise<string[]> {
  const tiers = await getTiers();
  const regions = new Set(tiers.map(t => t.region));
  return Array.from(regions).sort();
}

export async function getEntityCounts(): Promise<Record<string, number>> {
  const [tiers, upgradeRules, accessRules, csiMappings, promotions] = await Promise.all([
    getTiers(),
    getUpgradeRules(),
    getAccessRules(),
    getCsiMappings(),
    getPromotions(),
  ]);
  return {
    tiers: tiers.length,
    upgradeRules: upgradeRules.length,
    accessRules: accessRules.length,
    csiMappings: csiMappings.length,
    promotions: promotions.length,
    total: tiers.length + upgradeRules.length + accessRules.length + csiMappings.length + promotions.length,
  };
}
